import React from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Center, Loader, Box, Text } from '@mantine/core';
import { useAuth, usePermissions } from '../../hooks/useAuth';
import type { UserRole } from '../../types/auth';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAuth?: boolean;
  allowedRoles?: UserRole[];
  redirectTo?: string;
  fallback?: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  allowedRoles,
  redirectTo = '/login',
  fallback,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { hasAnyRole, currentRole } = usePermissions();
  const location = useLocation();

  // Show loading while auth state is being determined
  if (isLoading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Box ta="center">
          <Loader size="lg" color="admin" />
          <Text mt="md" c="dimmed">
            Loading...
          </Text>
        </Box>
      </Center>
    );
  }

  // Additional safeguard: if user is authenticated but role is not yet available, show loading
  if (isAuthenticated && user && allowedRoles && allowedRoles.length > 0) {
    const userRole = user.role_info?.value || user.role;
    if (!userRole) {
      if (import.meta.env.DEV) {
        console.log('ProtectedRoute: User authenticated but no role available', {
          user,
          userRole,
          role_info: user.role_info,
          role: user.role,
          allowedRoles
        });
      }
      return (
        <Center style={{ height: '100vh' }}>
          <Box ta="center">
            <Loader size="lg" color="admin" />
            <Text mt="md" c="dimmed">
              Loading user permissions...
            </Text>
            {import.meta.env.DEV && (
              <Text size="xs" c="dimmed" mt="xs">
                Debug: User role is undefined
              </Text>
            )}
          </Box>
        </Center>
      );
    }
  }

  // Check authentication requirement
  if (requireAuth && !isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0 && isAuthenticated) {
    if (!hasAnyRole(allowedRoles)) {
      // User is authenticated but doesn't have required role
      if (fallback) {
        return <>{fallback}</>;
      }

      return (
        <Center style={{ height: '100vh' }}>
          <Box ta="center">
            <Text size="xl" fw={600} c="red">
              Access Denied
            </Text>
            <Text mt="sm" c="dimmed">
              You don't have permission to access this page.
            </Text>
            <Text size="sm" c="dimmed">
              Your role: {currentRole || 'Unknown'}
            </Text>
            <Text size="sm" c="dimmed">
              Required roles: {allowedRoles.join(', ')}
            </Text>
          </Box>
        </Center>
      );
    }
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};

// Specific route guards for common use cases
export const AdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
    {children}
  </ProtectedRoute>
);

export const TeacherRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRoles={['super_admin', 'admin', 'teacher']}>
    {children}
  </ProtectedRoute>
);

export const OperatorRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRoles={['super_admin', 'admin', 'operator']}>
    {children}
  </ProtectedRoute>
);

export const ParentRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRoles={['parent']}>
    {children}
  </ProtectedRoute>
);

export const StaffRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute allowedRoles={['super_admin', 'admin', 'teacher', 'operator']}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
